import { globalStyleObj } from "@/app/assets/styles";
import {
  CommonTextAreaField,
  CommonTextInputField,
  FileReuseDialog,
  LabelText,
  SVGBannerImage,
} from "@/components";
import { TableCell } from "@/components/ui/table";
import { useFormatISODate } from "@/lib/hooks";
// Zod Validation Schemas
import { AllBlogsSchema, CareerSchema, TestimonialSchema } from "@/schemas";
import { MovieGalleryValidationSchema } from "@/schemas/pagesSchema/media/MovieGalleryValidationSchema";
import { PhotoGalleryValidationSchema } from "@/schemas/pagesSchema/media/PhotoGalleryValidationSchema";
import { VideoGalleryValidationSchema } from "@/schemas/pagesSchema/media/VideoGalleryValidationSchema";
import { NotificationValidationSchema } from "@/schemas/pagesSchema/NotificationvalidationSchema";
import Image from "next/image";

// NOTE Get image full URL
// export const getImageFullUrl = (url) => {
//   if (!url) return null;

//   return `/${process.env.NEXT_PUBLIC_LOCAL_UPLOAD_BASE_PATH}/${url}`;
// };
export const getImageFullUrl = (url) => {
  if (!url) return null;

  return `${process.env.NEXT_PUBLIC_DOMAIN_URL}/api/uploads/${url}`;
};

/*
  NOTE -------- React Hook Form Helper Input Fields -------
*/

// Get Media Validation Schema
export const getMediaValidationSchema = (type) => {
  if (type === "PHOTO") {
    return PhotoGalleryValidationSchema;
  } else if (type === "VIDEO") {
    return VideoGalleryValidationSchema;
  } else if (type === "MOVIE") {
    return MovieGalleryValidationSchema;
  } else if (type === "CAREER") {
    return CareerSchema;
  } else if (type === "BLOG") {
    return AllBlogsSchema;
  } else if (type === "TESTIMONIAL") {
    return TestimonialSchema;
  } else if (type === "NOTIFICATION") {
    return NotificationValidationSchema;
  } else {
    return null;
  }
};

// Get text input field (React Hook Form Field)
export const getTextInputField = ({
  field,
  control,
  errors,
  isItemDetailsExist,
}) => {
  return (
    <CommonTextInputField
      fieldName={field.fieldName}
      fieldType={field.fieldType}
      fieldId={`${isItemDetailsExist ? `update-${field.fieldId}` : `${field.fieldId}`}`}
      control={control}
      errors={errors}
      errorsType={errors?.[field.fieldName]}
      placeholderText={field?.placeholderText || ""}
      labelName={field?.lableName || ""}
      labelStatus={field?.labelStatus || false}
      translateField={field?.translateField || false}
      extraClass={field?.extraClasses || ""}
      extraInformationText={field?.extraInformationText || ""}
    />
  );
};

// Get text input field (React Hook Form Field)
export const getTextAreaInputField = ({
  field,
  control,
  errors,
  isItemDetailsExist,
}) => {
  return (
    <CommonTextAreaField
      fieldName={field.fieldName}
      fieldId={`${isItemDetailsExist ? `update-${field.fieldId}` : `${field.fieldId}`}`}
      control={control}
      errors={errors}
      errorsType={errors?.[field.fieldName]}
      labelName={field?.lableName || ""}
      labelStatus={field?.labelStatus || false}
      translateField={field?.translateField || false}
      extraClass={field?.extraClasses || ""}
    />
  );
};

// Get image input field (React Hook Form Field)
export const getImageInputField = ({
  errors,
  isItemDetailsExist,
  field,
  userId,
  filesList,
  allFiles,
  paginationDetails,
  searchValue,
  selectedFileType,
  adminRole,
  permissionsList,
  onChangeImageFiled,
  itemDetails = {},
}) => {
  return (
    <div
      className={`${field.extraClasses} ${globalStyleObj.commonInputContainerClass}`}
    >
      <LabelText
        text={field.lableName}
        htmlForId={`${isItemDetailsExist ? `update-${field.fieldId}` : `${field.fieldId}`}`}
        star={field.labelStatus}
      />
      <div className="flex flex-col gap-2 w-full max-w-[800px]">
        {!isItemDetailsExist ? (
          <FileReuseDialog
            htmlId={field.fieldId}
            userId={userId}
            filesList={filesList}
            allFiles={allFiles}
            paginationDetails={paginationDetails}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            onChangeBannerImage={(id) =>
              onChangeImageFiled(field.fieldName, id)
            }
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        ) : (
          <FileReuseDialog
            htmlId={`update-${field.fieldId}`}
            userId={userId}
            filesList={filesList}
            allFiles={allFiles}
            paginationDetails={paginationDetails}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            onChangeBannerImage={(id) =>
              onChangeImageFiled(field.fieldName, id)
            }
            selectedBannerFileId={itemDetails?.[field.fieldName]?._id ?? ""}
            selectedBannerFileName={
              itemDetails?.[field.fieldName]?.fileName ?? ""
            }
            adminRole={adminRole}
            permissionsList={permissionsList}
          />
        )}
        {errors && errors[field.fieldName] && (
          <p className="text-red-500 text-[13px] font-poppins-rg">
            {errors[field.fieldName].message}
          </p>
        )}
      </div>
    </div>
  );
};

/*
  NOTE -------- Table Rows Helper Fields -------
*/

// Date Table Row Field
export const getDateTableField = (
  item,
  statusName = "",
  keyName = "updatedAt",
  extraClass = ""
) => {
  return (
    <TableCell
      className={`font-poppins-md ${extraClass} ${item?.[statusName] ? "" : "line-through opacity-50"} hidden sm:table-cell`}
    >
      {item?.[keyName] ? useFormatISODate(item[keyName]) : "N/A"}
    </TableCell>
  );
};

// Image Table Row Field
export const getImageTableField = (
  item,
  statusName = "",
  keyName = "image",
  extraClass = ""
) => {
  return (
    <TableCell className={`${extraClass}`}>
      <div
        className={`w-[40px] h-[40px] sm:w-[100px] sm:h-[60px] rounded-full sm:rounded-md overflow-hidden relative ${item?.[statusName] ? "" : "opacity-50"} border dark:border-[#fff]/10`}
      >
        {item?.[keyName] &&
        item?.[keyName]?.fileType.startsWith("image/svg+xml") ? (
          <SVGBannerImage bannerUrl={getImageFullUrl(item[keyName].fileUrl)} />
        ) : (
          <Image
            src={
              item?.[keyName]
                ? getImageFullUrl(item[keyName].fileUrl)
                : "/assets/error400-cover.png"
            }
            alt={item?.[keyName] ? item?.[keyName]?.fileName : "Default Image"}
            fill
            sizes="(max-width: 576px) 100px, 150px"
            loading="lazy"
            unoptimized
            className="object-cover"
          />
        )}
      </div>
    </TableCell>
  );
};

export const getTextTableField = (
  item,
  statusName = "",
  keyName = "title",
  extraClass = ""
) => {
  return (
    <TableCell
      className={`font-poppins-md ${extraClass} ${item?.[statusName] ? "" : "line-through opacity-50"} hidden sm:table-cell`}
    >
      {item?.[keyName]
        ? item[keyName].length > 50
          ? `${item[keyName].slice(0, 50)} ...`
          : item[keyName]
        : "N/A"}
    </TableCell>
  );
};

// Single Date Formating - (01 Jan 2025)
export function formatDate(dateString) {
  if (!dateString) return "";

  // const date = new Date(dateString);

  // DEBUG Format: 14 Oct 2025
  // return date.toLocaleDateString("en-GB", {
  //   day: "2-digit",
  //   month: "short",
  //   year: "numeric",
  // });

  const [year, month, day] = dateString.split("-");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return `${day} ${months[parseInt(month, 10) - 1]} ${year}`;
}

// ✅ Utility function to format ISO date (ddth mmm yyyy)
export const formatISODateString = (isoString) => {
  const date = new Date(isoString);

  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  // Add suffix to day
  const getDaySuffix = (d) => {
    if (d > 3 && d < 21) return "th"; // 4–20
    switch (d % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${day}${getDaySuffix(day)} ${month} ${year}`;
};

export const formatTimeTo12Hour = (timeString) => {
  if (!timeString) return "";

  // If already in 12-hour format (contains AM/PM), just reformat with '.'
  if (/AM|PM/i.test(timeString)) {
    const [time, period] = timeString.trim().split(/\s+/);
    const [hourStr, minuteStr] = time.split(":");
    return `${parseInt(hourStr, 10)}.${minuteStr} ${period.toUpperCase()}`;
  }

  // Handle 24-hour format
  const [hourStr, minuteStr] = timeString.split(":");
  let hours = parseInt(hourStr, 10);
  const minutes = parseInt(minuteStr, 10);

  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${hours}.${minutes.toString().padStart(2, "0")} ${period}`;
};
